import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import Dropdown from "react-bootstrap/Dropdown";
import appConfig from "../../appConfig";
import { fetchSecure, getToken } from "../../services/apiService";
import SectionView from "./SectionView";
import { TestSetPanel, useTestSetNav } from "./TestSetPanel";
import { SOURCE_STYLES, issueCounts } from "./bomView";
import {
  CodeStringLine,
  CopyLinkButton,
  DownloadStatus,
  ErrorCard,
  IssueBadge,
  LoadingCard,
  SectionTabs,
  ViewSwitch,
  runDetails,
  useDocumentTitle,
  useDownload,
  useViewerParams,
} from "./viewerParts";
import "../bomScheduler/FileUpload.css";
import "./BomViewer.css";

const differences = (section) => (section.counts ? section.counts.changed + section.counts.added + section.counts.deleted : 0);

// The two Raw BOMs of a run compared row by row (Infor vs Infor), opened from the inputs dialog in a new tab.
// Same table, tree and filters as the BOM viewer, with each row marked changed, added, deleted or unchanged.
export default function ComparisonViewerPage() {
  const { inputId } = useParams();
  const { instance, accounts } = useMsal();
  const [params, setParam] = useViewerParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setState((prev) => ({ view: prev.view, loading: true }));
      const token = await getToken(instance, accounts);
      const response = token ? await fetchSecure(token, `${appConfig.apiUrl}outputs/comparison/${inputId}`) : null;
      if (cancelled) return;
      if (response?.status === "OK") setState({ view: response.data });
      else setState({ error: response?.message || "The comparison could not be loaded. Check your connection and try again." });
    };
    load();
    return () => {
      cancelled = true;
    };
    // accounts changes identity on every render of MsalProvider; the id is what matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputId]);

  const view = state.view;
  useDocumentTitle(view ? `${view.input.testSet} · ${view.a.shortLabel} vs ${view.b.shortLabel}` : "BOM comparison");
  const nav = useTestSetNav({ uploadId: view?.input.uploadId, inputId: Number(inputId), kind: "compare" });

  if (state.loading && !view) return <LoadingCard text="Comparing the BOMs..." />;
  if (state.error) return <ErrorCard text={state.error} />;

  const sides = { a: view.a, b: view.b };
  const issueTab = view.sections.find((s) => issueCounts(s, "a").notFound + issueCounts(s, "a").error + issueCounts(s, "b").notFound + issueCounts(s, "b").error > 0);
  const sectionName = params.get("tab");
  const section =
    view.sections.find((s) => s.name === sectionName) || (params.get("issues") === "1" && issueTab) || view.sections[0];

  return (
    <div className="bom-viewer text-start">
      <ComparisonHeader view={view} instance={instance} accounts={accounts} nav={nav} />
      <div className="bom-viewer__layout">
        <TestSetPanel nav={nav} />
        <div className="bom-viewer__main" aria-busy={state.loading ? "true" : undefined}>
          <SectionTabs
            sections={view.sections}
            current={section}
            onSelect={(s) => setParam("tab", s === view.sections[0] ? null : s.name)}
            badge={(s) => (
              <>
                {s.compared ? (
                  differences(s) > 0 ? (
                    <span className="bom-viewer__diffs" title={`${differences(s)} differences`}>{differences(s).toLocaleString()}</span>
                  ) : (
                    <span className="bom-viewer__count" title="No differences">0</span>
                  )
                ) : (
                  <span className="bom-viewer__count" title="Not compared">-</span>
                )}
                <IssueBadge counts={issueCounts(s, "a")} />
              </>
            )}
          />
          {section ? (
            <SectionView key={`${view.inputId}-${section.name}`} section={section} source="Comparison" params={params} setParam={setParam} sides={sides} />
          ) : (
            <div className="card border-0 shadow-sm"><div className="card-body text-muted">The files have no tabs.</div></div>
          )}
        </div>
      </div>
    </div>
  );
}

function ComparisonHeader({ view, instance, accounts, nav }) {
  const [download, downloadFile] = useDownload(instance, accounts);
  const input = view.input;
  const total = view.sections.reduce((sum, s) => sum + differences(s), 0);
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body p-3 p-md-4">
        <Link to="/" className="small">&larr; Back to BOM Compare</Link>
        <div className="d-flex flex-wrap align-items-start gap-3 mt-2">
          <div className="me-auto">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className={`bom-source ${SOURCE_STYLES[view.a.source] || ""}`}>{view.a.label}</span>
              <span className="text-muted small">vs</span>
              <span className={`bom-source ${SOURCE_STYLES[view.b.source] || ""}`}>{view.b.label}</span>
              <h3 className="upload-panel__title mb-0">{input.testSet}</h3>
            </div>
            <div className="bom-viewer__meta">
              {[...runDetails(input, "Infor"), total === 0 ? "No differences" : `${total.toLocaleString()} ${total === 1 ? "difference" : "differences"}`].join(" · ")}
            </div>
            <CodeStringLine input={input} />
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <ViewSwitch links={view.links} current={{ kind: "compare", id: view.inputId }} />
            <CopyLinkButton />
            <DownloadStatus status={download} />
            {view.files.length === 1 && (
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadFile(view.files[0].outputId, view.files[0].fileName)} disabled={download === "Downloading..."}>
                Download Excel
              </button>
            )}
            {view.files.length > 1 && (
              <Dropdown align="end">
                <Dropdown.Toggle size="sm" variant="outline-primary" disabled={download === "Downloading..."}>
                  Download Excel
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {view.files.map((file) => (
                    <Dropdown.Item key={file.outputId} as="button" onClick={() => downloadFile(file.outputId, file.fileName)}>
                      {file.fileName}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
